import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Typography, message } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, CloseOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { db } from '../../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { theme } from '../../../styles/theme';
import { trackUserAction } from '../../../utils/analytics';

const { Title, Text } = Typography;

// Function to get user's IP address
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP:', error);
    return 'unknown';
  }
};

// Function to get location from IP
const getLocationFromIP = async (ip) => {
  try {
    const response = await fetch(`https://ipapi.co/${ip}/json/`);
    const data = await response.json();
    return {
      city: data.city || 'unknown',
      region: data.region || 'unknown',
      country: data.country_name || 'unknown',
      countryCode: data.country_code || 'unknown',
      timezone: data.timezone || 'unknown',
      latitude: data.latitude || null,
      longitude: data.longitude || null
    };
  } catch (error) {
    console.error('Error fetching location:', error);
    return {
      city: 'unknown',
      region: 'unknown',
      country: 'unknown',
      countryCode: 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      latitude: null,
      longitude: null
    };
  }
};

// Function to get device and browser information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  const languages = navigator.languages;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const colorDepth = window.screen.colorDepth;
  const pixelRatio = window.devicePixelRatio;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const referrer = document.referrer || 'direct';
  const onlineStatus = navigator.onLine;

  // Detect device type
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android/i.test(userAgent) && !isMobile;
  const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  // Detect browser
  let browser = 'unknown';
  if (userAgent.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (userAgent.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (userAgent.indexOf('Safari') > -1) browser = 'Safari';
  else if (userAgent.indexOf('Edge') > -1) browser = 'Edge';
  else if (userAgent.indexOf('Opera') > -1) browser = 'Opera';

  // Detect OS
  let os = 'unknown';
  if (userAgent.indexOf('Windows') > -1) os = 'Windows';
  else if (userAgent.indexOf('Mac') > -1) os = 'macOS';
  else if (userAgent.indexOf('Linux') > -1) os = 'Linux';
  else if (userAgent.indexOf('Android') > -1) os = 'Android';
  else if (userAgent.indexOf('iOS') > -1 || userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) os = 'iOS';

  return {
    userAgent,
    platform,
    language,
    languages: languages || [language],
    screenResolution: `${screenWidth}x${screenHeight}`,
    windowSize: `${windowWidth}x${windowHeight}`,
    colorDepth,
    pixelRatio,
    timezone,
    referrer,
    onlineStatus,
    deviceType,
    browser,
    os,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || 'unknown'
  };
};

const BookingModal = ({ visible, onClose, source = 'general' }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleClose = () => {
    form.resetFields();
    setShowSuccess(false);
    onClose();
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Get device and browser information (synchronous, always available)
      const deviceInfo = getDeviceInfo();
      
      // Get user IP address and location (non-blocking, with timeout)
      let userIP = 'unknown';
      let locationData = {
        city: 'unknown',
        region: 'unknown',
        country: 'unknown',
        countryCode: 'unknown',
        timezone: deviceInfo.timezone,
        latitude: null,
        longitude: null
      };

      // Try to get IP and location with timeout
      try {
        const ipPromise = getUserIP();
        const ipTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('IP timeout')), 3000)
        );
        userIP = await Promise.race([ipPromise, ipTimeout]);
        
        if (userIP && userIP !== 'unknown') {
          try {
            const locationPromise = getLocationFromIP(userIP);
            const locationTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Location timeout')), 3000)
            );
            locationData = await Promise.race([locationPromise, locationTimeout]);
          } catch (locError) {
            console.warn('Location fetch failed:', locError);
            // Use fallback location data already set
          }
        }
      } catch (ipError) {
        console.warn('IP fetch failed:', ipError);
        // Continue with 'unknown' IP
      }
      
      const bookingData = {
        // User Information
        fullName: values.fullName,
        phone: values.phone,
        email: values.email || '',
        
        // Booking Information
        source: source,
        status: 'pending',
        
        // IP and Location
        ipAddress: userIP,
        location: {
          city: locationData.city,
          region: locationData.region,
          country: locationData.country,
          countryCode: locationData.countryCode,
          timezone: locationData.timezone,
          coordinates: locationData.latitude && locationData.longitude ? {
            latitude: locationData.latitude,
            longitude: locationData.longitude
          } : null
        },
        
        // Device and Browser Information
        deviceInfo: {
          deviceType: deviceInfo.deviceType,
          browser: deviceInfo.browser,
          os: deviceInfo.os,
          platform: deviceInfo.platform,
          userAgent: deviceInfo.userAgent,
          screenResolution: deviceInfo.screenResolution,
          windowSize: deviceInfo.windowSize,
          colorDepth: deviceInfo.colorDepth,
          pixelRatio: deviceInfo.pixelRatio,
          language: deviceInfo.language,
          languages: deviceInfo.languages,
          timezone: deviceInfo.timezone,
          referrer: deviceInfo.referrer,
          onlineStatus: deviceInfo.onlineStatus,
          cookieEnabled: deviceInfo.cookieEnabled,
          doNotTrack: deviceInfo.doNotTrack
        },
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('Submitting booking data:', bookingData);
      const docRef = await addDoc(collection(db, 'bookings'), bookingData);
      console.log('Booking submitted successfully with ID:', docRef.id);
      
      // Show success state in modal
      setShowSuccess(true);
      
      // Track the booking submission
      trackUserAction('booking_form_submitted', {
        source: source,
        hasEmail: !!values.email
      });
      
      // Show success message after modal closes
      setTimeout(() => {
        message.success('Your booking has been confirmed! Someone from our team will be reaching out to you shortly.', 5);
      }, 2000);
      
      // Close modal after showing success
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting booking:', error);
      message.error({
        content: 'Failed to submit booking. Please try again.',
        duration: 5,
        style: {
          marginTop: '20vh',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={false}
      width={isMobile ? '90%' : 520}
      centered
      styles={{
        body: {
          padding: 0,
          borderRadius: theme.borderRadius.xl,
          overflow: 'hidden',
          background: 'transparent'
        },
        content: {
          padding: 0,
          background: 'transparent'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      {/* Header Section */}
      <div style={{
            background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
            padding: isMobile ? '24px 20px' : '32px 32px',
            position: 'relative',
            overflow: 'visible'
          }}>
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '120px',
              height: '120px',
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '50%',
              zIndex: 0,
              pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '80px',
              height: '80px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0,
              pointerEvents: 'none'
            }} />
            
            {/* Close button */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: isMobile ? '12px' : '16px',
                right: isMobile ? '12px' : '16px',
                color: theme.colors.white,
                fontSize: '18px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                padding: 0,
                minWidth: '36px'
              }}
            />
          
          {/* Title */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <Title 
              level={2} 
              style={{ 
                color: theme.colors.white, 
                margin: 0,
                fontFamily: theme.fonts.primary,
                fontWeight: 700,
                fontSize: isMobile ? '24px' : '28px'
              }}
            >
              Book Your Service
            </Title>
            <Text style={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: '14px',
              fontFamily: theme.fonts.primary,
              display: 'block',
              marginTop: '8px'
            }}>
              Fill out the form below and we'll get back to you soon
            </Text>
          </div>
        </div>

        {/* Success Section */}
        {showSuccess ? (
          <div style={{
            padding: isMobile ? '40px 20px' : '60px 32px',
            background: theme.colors.white,
            textAlign: 'center'
          }}>
            <CheckCircleOutlined style={{
              fontSize: '64px',
              color: theme.colors.success,
              marginBottom: '24px',
              display: 'block'
            }} />
            <Title 
              level={3}
              style={{
                color: theme.colors.black,
                fontFamily: theme.fonts.primary,
                fontWeight: 700,
                marginBottom: '16px'
              }}
            >
              Booking Confirmed!
            </Title>
            <Text style={{
              fontSize: '16px',
              color: theme.colors.gray,
              fontFamily: theme.fonts.primary,
              display: 'block',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              Your booking has been confirmed! Someone from our team will be reaching out to you shortly.
            </Text>
          </div>
        ) : (
          /* Form Section */
          <div style={{
            padding: isMobile ? '24px 20px' : '32px',
            background: theme.colors.white
          }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
            <Form.Item
              name="fullName"
              label={
                <span style={{ 
                  fontFamily: theme.fonts.primary, 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: theme.colors.black
                }}>
                  Full Name
                </span>
              }
              rules={[{ required: true, message: 'Please enter your full name' }]}
              style={{ marginBottom: '20px' }}
            >
              <Input 
                placeholder="Enter your full name" 
                size="large"
                prefix={<UserOutlined style={{ color: theme.colors.primary, opacity: 0.6 }} />}
                className="booking-modal-input"
                style={{
                  borderRadius: theme.borderRadius.md,
                  fontFamily: theme.fonts.primary,
                  fontSize: '15px',
                  padding: '10px 12px',
                  border: 'none',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: `2px solid ${theme.colors.borderGray}`,
                  transition: 'all 0.3s ease',
                  boxShadow: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.primary, 'important');
                  e.target.style.setProperty('box-shadow', 'none', 'important');
                }}
                onBlur={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.borderGray, 'important');
                }}
                bordered={false}
              />
            </Form.Item>

            <Form.Item
              name="phone"
              label={
                <span style={{ 
                  fontFamily: theme.fonts.primary, 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: theme.colors.black
                }}>
                  Phone Number
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your phone number' },
                { pattern: /^[\d\s\-\+\(\)]+$/, message: 'Please enter a valid phone number' }
              ]}
              style={{ marginBottom: '20px' }}
            >
              <Input 
                placeholder="Enter your phone number" 
                size="large"
                prefix={<PhoneOutlined style={{ color: theme.colors.primary, opacity: 0.6 }} />}
                className="booking-modal-input"
                style={{
                  borderRadius: theme.borderRadius.md,
                  fontFamily: theme.fonts.primary,
                  fontSize: '15px',
                  padding: '10px 12px',
                  border: 'none',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: `2px solid ${theme.colors.borderGray}`,
                  transition: 'all 0.3s ease',
                  boxShadow: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.primary, 'important');
                  e.target.style.setProperty('box-shadow', 'none', 'important');
                }}
                onBlur={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.borderGray, 'important');
                }}
                bordered={false}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={
                <span style={{ 
                  fontFamily: theme.fonts.primary, 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: theme.colors.black
                }}>
                  Email <span style={{ color: theme.colors.gray, fontWeight: 400 }}>(Optional)</span>
                </span>
              }
              rules={[
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
              style={{ marginBottom: '28px' }}
            >
              <Input 
                placeholder="Enter your email (optional)" 
                size="large"
                prefix={<MailOutlined style={{ color: theme.colors.primary, opacity: 0.6 }} />}
                className="booking-modal-input"
                style={{
                  borderRadius: theme.borderRadius.md,
                  fontFamily: theme.fonts.primary,
                  fontSize: '15px',
                  padding: '10px 12px',
                  border: 'none',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderBottom: `2px solid ${theme.colors.borderGray}`,
                  transition: 'all 0.3s ease',
                  boxShadow: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.primary, 'important');
                  e.target.style.setProperty('box-shadow', 'none', 'important');
                }}
                onBlur={(e) => {
                  e.target.style.setProperty('border-bottom-color', theme.colors.borderGray, 'important');
                }}
                bordered={false}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isSubmitting}
                size="large"
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: theme.borderRadius.md,
                  fontFamily: theme.fonts.primary,
                  fontWeight: 700,
                  fontSize: '16px',
                  background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
                  border: 'none',
                  boxShadow: `0 4px 12px rgba(253, 134, 92, 0.3)`,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 16px rgba(253, 134, 92, 0.4)`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 12px rgba(253, 134, 92, 0.3)`;
                }}
              >
                Book Now
              </Button>
            </Form.Item>
          </Form>
        </div>
        )}
    </Modal>
  );
};

export default BookingModal;

